import { API_CONFIG, AUTH_CONFIG } from '../config';

let API_BASE = API_CONFIG.BASE_URL;
const TOKEN_STORAGE_KEY = AUTH_CONFIG.TOKEN_KEY;
const REQUEST_TIMEOUT = API_CONFIG.TIMEOUT;

// 租户相关存储 Key
const TENANT_ID_KEY = 'current_tenant_id';
const ENTERPRISE_ID_KEY = 'current_enterprise_id';

/**
 * 获取当前租户 ID
 */
export const getCurrentTenantId = () => {
  return localStorage.getItem(TENANT_ID_KEY);
};

/**
 * 获取当前企业 ID
 */
export const getCurrentEnterpriseId = () => {
  return localStorage.getItem(ENTERPRISE_ID_KEY);
};

/**
 * 设置当前租户 ID
 */
export const setCurrentTenantId = (tenantId) => {
  if (tenantId) {
    localStorage.setItem(TENANT_ID_KEY, tenantId);
  } else {
    localStorage.removeItem(TENANT_ID_KEY);
  }
};

/**
 * 设置当前企业 ID
 */
export const setCurrentEnterpriseId = (enterpriseId) => {
  if (enterpriseId) {
    localStorage.setItem(ENTERPRISE_ID_KEY, enterpriseId);
  } else {
    localStorage.removeItem(ENTERPRISE_ID_KEY);
  }
};

/**
 * 检测是否在iframe中，如果是则使用绝对URL用于API请求
 * 这样可以确保iframe中的请求能正确发送到后端，避免跨域问题
 */
const initializeApiBase = () => {
  // 检测是否在iframe中
  const isInIframe = window.self !== window.top;

  if (isInIframe) {
    // 在iframe中时，使用绝对URL指向后端
    // 从当前URL推断后端地址（同一主机下）
    const currentOrigin = window.location.origin;
    const backendPort = process.env.REACT_APP_BACKEND_PORT || '9090';

    // 如果是localhost开发环境，固定为9090端口
    if (currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1')) {
      API_BASE = `http://localhost:${backendPort}${API_CONFIG.BASE_URL}`;
    } else {
      // 生产环境：使用同源的相对路径
      API_BASE = API_CONFIG.BASE_URL;
    }

    console.log('🔄 [API] 在iframe中运行，已调整API_BASE:', API_BASE);
  }
};

initializeApiBase();

export const getStoredToken = () => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to read auth token from storage:', error);
    return null;
  }
};

export const setStoredToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch (error) {
    console.error('Failed to persist auth token:', error);
  }
};

const buildQueryString = (params) => {
  if (!params) {
    return '';
  }
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      query.append(key, value);
    }
  });
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
};

// 请求拦截器队列
const requestInterceptors = [];
// 响应拦截器队列
const responseInterceptors = [];

// 添加请求拦截器
export const addRequestInterceptor = (interceptor) => {
  requestInterceptors.push(interceptor);
};

// 添加响应拦截器
export const addResponseInterceptor = (interceptor) => {
  responseInterceptors.push(interceptor);
};

// 执行请求拦截器
const runRequestInterceptors = async (config) => {
  let finalConfig = config;
  for (const interceptor of requestInterceptors) {
    finalConfig = await interceptor(finalConfig);
  }
  return finalConfig;
};

// 执行响应拦截器
const runResponseInterceptors = async (response) => {
  let finalResponse = response;
  for (const interceptor of responseInterceptors) {
    finalResponse = await interceptor(finalResponse);
  }
  return finalResponse;
};

export async function request(path, { method = 'GET', headers, body, params, skipAuth = false, skipBaseUrl = false, skipTenant = false, timeout = REQUEST_TIMEOUT } = {}) {
  const token = skipAuth ? null : getStoredToken();
  const requestHeaders = new Headers(headers || {});

  let requestBody = body;
  if (body && !(body instanceof FormData) && !(body instanceof URLSearchParams) && typeof body === 'object') {
    requestHeaders.set('Content-Type', 'application/json');
    requestBody = JSON.stringify(body);
  }

  if (token) {
    requestHeaders.set('Authorization', `Bearer ${token}`);
  }

  // 添加租户 ID 和企业 ID 到请求头
  if (!skipTenant) {
    const tenantId = getCurrentTenantId();
    const enterpriseId = getCurrentEnterpriseId();
    if (tenantId) {
      requestHeaders.set('X-Tenant-Id', tenantId);
    }
    if (enterpriseId) {
      requestHeaders.set('X-Enterprise-Id', enterpriseId);
    }
  }

  // 请求配置
  // skipBaseUrl: true 时直接使用路径，不拼接 API_BASE（用于访问主系统接口）
  const url = skipBaseUrl
    ? `${path}${buildQueryString(params)}`
    : `${API_BASE}${path}${buildQueryString(params)}`;

  let config = {
    url,
    method,
    headers: requestHeaders,
    body: requestBody,
    credentials: 'include',
  };

  // 执行请求拦截器
  config = await runRequestInterceptors(config);

  // 添加超时控制
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(config.url, {
      ...config,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // 执行响应拦截器
    const interceptedResponse = await runResponseInterceptors(response.clone());

    const isJson = interceptedResponse.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await interceptedResponse.json() : null;

    if (!interceptedResponse.ok) {
      const message = payload?.msg || interceptedResponse.statusText || '请求失败';
      if (interceptedResponse.status === 401) {
        setStoredToken(null);
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      const error = new Error(message);
      error.response = { status: interceptedResponse.status, data: payload };
      throw error;
    }

    if (payload?.code && payload.code !== '200') {
      if (payload.code === '401') {
        setStoredToken(null);
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      const error = new Error(payload.msg || '请求失败');
      error.response = { status: parseInt(payload.code) || 500, data: payload };
      throw error;
    }

    return payload?.data !== undefined ? payload.data : payload;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      const timeoutError = new Error('请求超时');
      timeoutError.code = 'ECONNABORTED';
      throw timeoutError;
    }
    throw error;
  }
}

export const clearStoredToken = () => setStoredToken(null);

export const getApiBase = () => API_BASE;
